/**
 * VRSnapTurnComponent - Implements snap turning via VR thumbstick/touchpad.
 */

import { SceneComponent } from '../../components/SceneComponent.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { XRAxis } from '../VRControllerManager.js';
import { VRPawn } from '../VRPawn.js';

import type { SceneComponentOptions } from '../../components/SceneComponent.js';
import type { IVRInputHandler } from '../VRPawn.js';

export interface VRSnapTurnOptions extends SceneComponentOptions {
  angleDeg?: number;
  threshold?: number;
  cooldownMs?: number;
  axis?: XRAxis;
  handedness?: 'left' | 'right' | 'any';
}

@EngineClass('VRSnapTurnComponent')
export class VRSnapTurnComponent extends SceneComponent implements IVRInputHandler {


  @property({ type: 'number', min: 1, max: 180, step: 1, unit: 'deg' })
  public angleDeg: number = 45;

  @property({ type: 'number', min: 0, max: 1, step: 0.01 })
  public threshold: number = 0.7;

  @property({ type: 'number', min: 0, max: 2000, step: 10, unit: 'ms' })
  public cooldownMs: number = 300;

  @property({ type: 'enum', options: Object.values(XRAxis) })
  public axis: XRAxis = XRAxis.ThumbstickX;

  @property()
  public handedness: 'left' | 'right' | 'any' = 'any';


  private pawn: VRPawn | null = null;
  private lastSnapTimeByHand: Map<'left' | 'right', number> = new Map();
  private armedDirectionByHand: Map<'left' | 'right', 0 | -1 | 1> = new Map();

  public override initialize(options?: VRSnapTurnOptions): void {
    super.initialize(options);
    this.angleDeg = options?.angleDeg ?? this.angleDeg;
    this.threshold = options?.threshold ?? this.threshold;
    this.cooldownMs = options?.cooldownMs ?? this.cooldownMs;
    this.axis = options?.axis ?? this.axis;
    this.handedness = options?.handedness ?? this.handedness;
  }



  public override beginPlay(): void {
    super.beginPlay();
    const actor = this.getActor();
    if (actor instanceof VRPawn) {
      this.pawn = actor;
      this.pawn.addVRInputHandler(this, 10);
    }
  }

  public override endPlay(): void {
    if (this.pawn) {
      this.pawn.removeVRInputHandler(this);
    }
    this.pawn = null;
    super.endPlay();
  }

  public handleVRAxisChange(inputSource: XRInputSource, axis: XRAxis, value: number): boolean {
    if (!this.pawn) return false;
    if (axis !== this.axis) return false;

    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (this.handedness !== 'any' && hand !== this.handedness) return false;
    if (hand === 'none') return false;

    const now = Date.now();
    const angle = Math.abs(this.angleDeg);

    const magnitude = Math.abs(value);
    const dir: 0 | -1 | 1 = value > 0 ? 1 : value < 0 ? -1 : 0;

    const armed = this.armedDirectionByHand.get(hand) ?? 0;
    if (magnitude < 0.2) {
      this.armedDirectionByHand.set(hand, 0);
      return false;
    }

    if (magnitude > this.threshold && dir !== 0) {
      const lastTime = this.lastSnapTimeByHand.get(hand) ?? 0;
      if ((now - lastTime) < this.cooldownMs) return false;
      if (armed === dir) return false;

      const degrees = dir > 0 ? -angle : angle;
      this.pawn.yawByDegrees(degrees);
      this.lastSnapTimeByHand.set(hand, now);
      this.armedDirectionByHand.set(hand, dir);
      return true;
    }

    return false;
  }
}


